import React from 'react';
import { withFormsy } from 'formsy-react';
import { Chip, FormControl, Grid, InputLabel, Paper, Slider, styled, Typography } from '@mui/material';

import { type FieldProps } from './components/BaseTextField';

type RootProps = Pick<SliderFieldProps, 'name' | 'isEdit' | 'logProps'>;

type SliderFieldProps = {
	minValue?: number;
	maxValue?: number;
} & Omit<FieldProps<number>, 'startAdornment' | 'endAdornment'>;

const Root = styled('div')<RootProps>(({ isEdit, logProps, name, theme }) => {
	const hasLogs = logProps?.hasLogs(name);
	return {
		width: '100%',
		'& > .MuiFormControl-root > .MuiPaper-root': {
			padding: '20px 12px 0 12px',
			borderRadius: '4px 4px 0 0',
			borderBottom: isEdit
				? `1px solid ${theme.palette.action.active}`
				: hasLogs
					? '1px solid transparent'
					: `1px dotted ${theme.palette.action.active}`,
			backgroundColor: isEdit
				? theme.palette.action.selected
				: hasLogs
					? theme.palette.action.selected
					: 'transparent',
			'&:hover': {
				backgroundColor: isEdit
					? theme.palette.action.focus
					: hasLogs
						? theme.palette.action.focus
						: 'transparent',
				cursor: hasLogs ? 'pointer' : 'default'
			}
		}
	};
});

/** A component used to set a value by sliding an handle */
function SliderField({ minValue = 0, maxValue = 100, ...props }: SliderFieldProps) {
	const hasLogs = props.logProps?.hasLogs(props.name);
	const logsCounter = props.logProps?.getLogsCounter(props.name);
	const openLogsDialog = () => props.logProps?.openLogsDialog(props.name, props.label);

	function updateValue(_event, newValue) {
		props.setValue(newValue);
	}

	function handleChange() {
		if (props.beforeChange) props.beforeChange(props.value ?? undefined);
		props.sendChange(props.name, props.value || null);
		if (props.afterChange) props.afterChange(props.value ?? undefined);
	}

	return (
		<Root name={props.name} isEdit={props.isEdit} logProps={props.logProps}>
			<FormControl className={props.className} variant="filled" fullWidth>
				<InputLabel htmlFor="sliderField" shrink>
					{props.label}
				</InputLabel>
				<Paper id="sliderField" elevation={0} onClick={openLogsDialog}>
					<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
						<Grid size="grow">
							<Grid container justifyContent="space-between" alignItems="center" spacing={1}>
								<Grid>
									<Typography
										className="w-32"
										variant="subtitle2"
										color="text.primary"
										fontWeight={400}
									>
										{`${Math.round(props.value || 0)}%`}
									</Typography>
								</Grid>
								<Grid size="grow">
									<Slider
										className="py-12"
										name={props.name}
										value={props.value || 0}
										size="small"
										color="secondary"
										min={minValue}
										max={maxValue}
										onChange={updateValue}
										onChangeCommitted={handleChange}
										disabled={!props.isEdit}
									/>
								</Grid>
							</Grid>
						</Grid>
						{hasLogs && (
							<Grid>
								<Chip
									className="-mt-20 cursor-pointer"
									label={logsCounter}
									size="small"
									color="primary"
								/>
							</Grid>
						)}
					</Grid>
				</Paper>
			</FormControl>
		</Root>
	);
}

export default React.memo(withFormsy(SliderField));
